import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { Eye, Cpu, Shield, CheckCircle, Calendar, Phone, Award, ArrowRight, BookOpen, ExternalLink, Activity, Scan, Target, Zap } from 'lucide-react';

const Tecnologie = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const technologies = [
    {
      name: 'OCT (Tomografia a Coerenza Ottica)',
      description: 'Imaging ad alta risoluzione della retina e del nervo ottico',
      icon: Scan,
      details: [
        'Visualizzazione degli strati retinici in sezione',
        'Diagnosi precoce di maculopatia e glaucoma',
        'Monitoraggio della progressione delle patologie',
        'Misurazione precisa dello spessore retinico'
      ],
      applications: ['Maculopatia', 'Glaucoma', 'Retinopatia diabetica', 'Edema maculare']
    },
    {
      name: 'Retinografia',
      description: 'Fotografia ad alta definizione del fondo oculare',
      icon: Eye,
      details: [
        'Documentazione fotografica della retina',
        'Imaging grandangolare fino a 200°',
        'Autofluorescenza per patologie maculari',
        'Confronto nel tempo per follow-up'
      ],
      applications: ['Retinopatia diabetica', 'Degenerazione maculare', 'Distacco di retina', 'Nevi coroideali']
    },
    {
      name: 'Topografia Corneale',
      description: 'Mappatura tridimensionale della superficie corneale',
      icon: Target,
      details: [
        'Analisi della curvatura corneale',
        'Diagnosi di cheratocono e astigmatismo',
        'Pianificazione chirurgia refrattiva',
        'Fitting lenti a contatto speciali'
      ],
      applications: ['Cheratocono', 'Astigmatismo', 'Pre-LASIK', 'Lenti a contatto']
    },
    {
      name: 'Autorefrattometro',
      description: 'Misurazione automatica del difetto refrattivo',
      icon: Zap,
      details: [
        'Valutazione oggettiva di miopia, ipermetropia, astigmatismo',
        'Misurazione rapida e non invasiva',
        'Base per la prescrizione di occhiali',
        'Cheratometria integrata'
      ],
      applications: ['Miopia', 'Ipermetropia', 'Astigmatismo', 'Presbiopia']
    },
    {
      name: 'Tonometro',
      description: 'Misurazione della pressione intraoculare',
      icon: Activity,
      details: [
        'Screening per il glaucoma',
        'Tonometria ad applanazione (gold standard)',
        'Tonometria a soffio (non contact)',
        'Pachimetria corneale integrata'
      ],
      applications: ['Glaucoma', 'Ipertensione oculare', 'Follow-up post-chirurgico']
    },
    {
      name: 'Conta Endoteliale (Microscopia Speculare)',
      description: 'Analisi delle cellule endoteliali corneali',
      icon: Cpu,
      details: [
        'Conta delle cellule endoteliali',
        'Valutazione della morfologia cellulare',
        'Pre-operatorio cataratta',
        'Monitoraggio distrofie corneali'
      ],
      applications: ['Pre-cataratta', 'Distrofia di Fuchs', 'Trapianto corneale', 'Lenti fachiche']
    },
    {
      name: 'Campo Visivo Computerizzato',
      description: 'Perimetria statica per mappare il campo visivo',
      icon: Shield,
      details: [
        'Diagnosi e monitoraggio del glaucoma',
        'Rilevamento di scotomi (aree cieche)',
        'Valutazione patologie neurologiche',
        'Analisi statistica della progressione'
      ],
      applications: ['Glaucoma', 'Neuriti ottiche', 'Tumori cerebrali', 'Ictus']
    },
    {
      name: 'Ecografo Bulbare',
      description: 'Ultrasonografia del bulbo oculare e dell\'orbita',
      icon: Scan,
      details: [
        'Visualizzazione di strutture oculari opache',
        'Diagnosi di distacco di retina',
        'Misurazione biometrica per IOL',
        'Valutazione tumori intraoculari'
      ],
      applications: ['Cataratta matura', 'Distacco di retina', 'Tumori oculari', 'Emorragie vitreali']
    }
  ];

  const benefits = [
    { icon: Scan, title: 'Diagnosi Precisa', description: 'Tecnologie di imaging avanzate per diagnosi accurate e precoci' },
    { icon: Shield, title: 'Non Invasivo', description: 'Esami indolori e sicuri, senza contatto diretto con l\'occhio' },
    { icon: Activity, title: 'Monitoraggio', description: 'Follow-up accurato dell\'evoluzione delle patologie nel tempo' },
    { icon: Cpu, title: 'Digitalizzazione', description: 'Referti digitali, confronto storico e condivisione immediata' },
  ];

  const scientificArticles = [
    {
      title: 'Tomografia a Coerenza Ottica: Tecnologia e Applicazioni',
      authors: 'Huang D, Swanson EA, Lin CP, et al.',
      journal: 'Science',
      year: '1991',
      doi: '10.1126/science.1957169',
      summary: 'Articolo fondamentale che ha introdotto la tecnologia OCT in oftalmologia.'
    },
    {
      title: 'Il Ruolo della Topografia Corneale nella Chirurgia Refrattiva',
      authors: 'Rabinowitz YS.',
      journal: 'Current Opinion in Ophthalmology',
      year: '1998',
      doi: '10.1097/00055735-199808000-00005',
      summary: 'Review sull\'importanza della topografia corneale nella pianificazione chirurgica.'
    },
    {
      title: 'Test del Campo Visivo nel Glaucoma',
      authors: 'Sample PA, Bosworth CF, Blumenthal EZ, et al.',
      journal: 'Survey of Ophthalmology',
      year: '2000',
      doi: '10.1016/S0039-6257(00)00148-2',
      summary: 'Guida completa sulla perimetria computerizzata nel glaucoma.'
    },
    {
      title: 'Microscopia Speculare dell\'Endotelio Corneale',
      authors: 'Bourne WM, McLaren JW.',
      journal: 'American Journal of Ophthalmology',
      year: '2004',
      doi: '10.1016/j.ajo.2003.10.013',
      summary: 'Tecniche e applicazioni cliniche della microscopia speculare.'
    },
    {
      title: 'Ecografia Oftalmologica: Pratica Attuale e Sviluppi Futuri',
      authors: 'Silverman RH.',
      journal: 'Expert Review of Medical Devices',
      year: '2009',
      doi: '10.1586/erd.09.4',
      summary: 'Stato dell\'arte e prospettive future dell\'ecografia oculare.'
    },
    {
      title: 'Imaging Retinico Grandangolare: Una Revisione',
      authors: 'Nagiel A, Lalane RA, Sadda SR, Schwartz SD.',
      journal: 'Survey of Ophthalmology',
      year: '2016',
      doi: '10.1016/j.survophthal.2015.06.001',
      summary: 'Panoramica sulle tecnologie di imaging retinico grandangolare.'
    }
  ];

  const faq = [
    {
      question: 'Gli esami diagnostici sono dolorosi?',
      answer: 'No, tutti gli esami che utilizziamo sono completamente indolori e non invasivi. La maggior parte richiede solo che il paziente guardi un punto fisso mentre lo strumento acquisisce le immagini.'
    },
    {
      question: 'Quanto tempo richiedono gli esami?',
      answer: 'La durata varia da esame a esame: l\'OCT richiede circa 5-10 minuti, la topografia corneale 5 minuti, il campo visivo 15-20 minuti per occhio. Durante la visita completa vengono eseguiti solo gli esami necessari.'
    },
    {
      question: 'È necessaria la dilatazione della pupilla?',
      answer: 'Per alcuni esami come la retinografia e l\'OCT del polo posteriore può essere utile la dilatazione della pupilla. In questo caso, è consigliabile non guidare per alcune ore dopo la visita.'
    },
    {
      question: 'Ogni quanto vanno ripetuti gli esami?',
      answer: 'La frequenza dipende dalla patologia e dalla sua stabilità. Per il glaucoma, ad esempio, il campo visivo viene ripetuto ogni 6-12 mesi. L\'oculista definirà il piano di follow-up personalizzato.'
    },
    {
      question: 'I risultati sono immediati?',
      answer: 'Sì, tutti i nostri strumenti forniscono risultati in tempo reale. Il medico può visualizzare e interpretare i dati immediatamente durante la visita, confrontandoli con esami precedenti se disponibili.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232A9FCB' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <span 
                className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-6"
                style={{ background: 'rgba(42, 159, 203, 0.1)', color: '#2A9FCB' }}
              >
                Diagnostica Avanzata
              </span>
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
              >
                Tecnologia al Servizio dei Tuoi Occhi
              </h1>
              <p 
                className="text-lg sm:text-xl mb-8 leading-relaxed"
                style={{ fontFamily: "'Manrope', sans-serif", color: '#666666' }}
              >
                Strumentazione diagnostica di ultima generazione per una valutazione completa 
                e accurata della salute dei tuoi occhi. Dalla retina alla cornea, 
                monitoriamo ogni aspetto della tua vista.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/prenota" 
                  className="inline-flex items-center justify-center bg-[#2A9FCB] text-white hover:bg-[#1C729E] rounded-full px-8 py-4 text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Prenota Visita
                </Link>
                <a 
                  href="tel:+390123456789" 
                  className="inline-flex items-center justify-center border-2 border-[#2A9FCB] text-[#2A9FCB] hover:bg-[#E6F4FA] rounded-full px-8 py-4 text-sm font-medium transition-all duration-300"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Chiama Ora
                </a>
              </div>
            </div>
            
            <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <img 
                src="/api/static/treatments/tecnologia_vista.png"
                alt="Tecnologie diagnostiche oftalmologiche"
                className="rounded-3xl shadow-2xl w-full object-cover"
                style={{ maxHeight: '500px' }}
              />
              <div 
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl"
                style={{ maxWidth: '280px' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-[#E6F4FA] flex items-center justify-center">
                    <Cpu className="w-6 h-6 text-[#2A9FCB]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#2A9FCB]">8+</p>
                    <p className="text-sm text-gray-500">Strumenti Diagnostici</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">Tecnologia all'avanguardia per la tua vista</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
            >
              Perché la Diagnostica Avanzata
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#666666' }}>
              I vantaggi di una strumentazione all'avanguardia
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div 
                key={idx}
                className="p-8 rounded-2xl bg-gradient-to-br from-[#e8f4fd] via-[#f4f9fe] to-[#dcedf8] hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-transparent hover:border-[#2A9FCB]/20"
              >
                <div className="w-14 h-14 rounded-full bg-[#E6F4FA] flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 text-[#2A9FCB]" />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Grid Section */}
      <section className="py-20" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
            >
              La Nostra Strumentazione
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#666666' }}>
              Una panoramica completa delle tecnologie disponibili nel nostro centro
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {technologies.map((tech, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#2A9FCB] to-[#1C729E] flex items-center justify-center flex-shrink-0">
                    <tech.icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2" style={{ color: '#1A1A1A' }}>{tech.name}</h3>
                    <p className="text-gray-600">{tech.description}</p>
                  </div>
                </div>
                
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-500 mb-3">CARATTERISTICHE</h4>
                  <ul className="space-y-2">
                    {tech.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#2A9FCB] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="pt-4 border-t border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-500 mb-2">APPLICAZIONI</h4>
                  <div className="flex flex-wrap gap-2">
                    {tech.applications.map((app, i) => (
                      <span 
                        key={i}
                        className="px-3 py-1 bg-[#E6F4FA] text-[#2A9FCB] text-xs font-medium rounded-full"
                      >
                        {app}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scientific Articles Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <BookOpen className="w-8 h-8 text-[#2A9FCB]" />
              <h2 
                className="text-3xl sm:text-4xl"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
              >
                Letteratura Scientifica
              </h2>
            </div>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#666666' }}>
              Articoli e studi sulle tecnologie diagnostiche oftalmologiche
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scientificArticles.map((article, idx) => (
              <div 
                key={idx}
                className="bg-gradient-to-br from-[#e8f4fd] via-[#f4f9fe] to-[#dcedf8] rounded-2xl p-6 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2 line-clamp-2">{article.title}</h3>
                <p className="text-sm text-gray-600 mb-2 line-clamp-1">{article.authors}</p>
                <p className="text-sm text-[#2A9FCB] mb-3">
                  {article.journal}, {article.year}
                </p>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{article.summary}</p>
                <a 
                  href={`https://doi.org/${article.doi}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-[#2A9FCB] hover:underline"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  DOI: {article.doi}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
            >
              Domande Frequenti
            </h2>
          </div>
          
          <div className="space-y-4">
            {faq.map((item, idx) => (
              <details 
                key={idx}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 group"
              >
                <summary className="p-6 cursor-pointer flex items-center justify-between font-semibold text-[#1A1A1A] hover:text-[#2A9FCB] transition-colors">
                  {item.question}
                  <ArrowRight className="w-5 h-5 transform group-open:rotate-90 transition-transform text-[#2A9FCB]" />
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  {item.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2A9FCB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl sm:text-4xl text-white mb-6"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Prenota la Tua Visita Diagnostica
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Una valutazione completa con le migliori tecnologie disponibili. 
            La diagnosi precoce è il primo passo per proteggere la tua vista.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/prenota" 
              className="inline-flex items-center justify-center bg-white text-[#2A9FCB] hover:bg-gray-100 rounded-full px-10 py-4 text-sm font-semibold transition-all duration-300 shadow-lg"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Prenota Online
            </Link>
            <a 
              href="tel:+390123456789" 
              className="inline-flex items-center justify-center border-2 border-white text-white hover:bg-white/10 rounded-full px-10 py-4 text-sm font-semibold transition-all duration-300"
            >
              <Phone className="w-5 h-5 mr-2" />
              Chiama: 012 345 6789
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Tecnologie;
